import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { EnrichLeadResult, EnrichResult } from "./types";

/**
 * POST /api/campaigns/{id}/enrich — waterfall enrichment over APPROVED leads.
 * Found emails become contacts, so the lead queue + campaigns list (contacts
 * count) are invalidated on success.
 */
export function useEnrichCampaign(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      httpClient<EnrichResult>(`/api/campaigns/${campaignId}/enrich`, { method: "POST" }),
    onSuccess: () => {
      // Broad ["leads"] prefix so BOTH the browse list (["leads","browse",…]) that the
      // Enrichment screen reads and the review queue refresh with the new contacts.
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}

/**
 * POST /api/leads/{id}/enrich — on-demand (re-)enrichment of a SINGLE lead (drawer
 * action, ADR-0024). Always re-runs (server ignores `enrichedAt`); only APPROVED leads
 * are eligible. Invalidates the open lead detail + the lists so new contacts/company show.
 */
export function useEnrichLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      httpClient<EnrichLeadResult>(`/api/leads/${leadId}/enrich`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lead"] }); // the open drawer's detail
      qc.invalidateQueries({ queryKey: ["leads"] }); // browse list + review queue
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}
